import { AnyAction } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import { GlobalActions } from '@ps20-frontend/shop-integration-redux';
import { App, Immobilie, VwgVorbelegungdaten } from '@ps20-frontend/frontend-models';
import { VwgVorvertragsdatenMerkmale } from '@ps20/bff-models';
import { IAppState } from '../appState';
import {
  isDenkmalschutzSelectableByValue,
  isSchwimmbadSelectableByValue,
} from '../../app/shared/selectors/gebaeude.selector';
import { VwgActions } from '../vwgActions';

export interface Merkmale {
  keller: boolean;
  kellerMitWohnflaeche: boolean;
  holzhaus: boolean;
  dachbelag: boolean;
  flachdach: boolean;
  geothermie: boolean;
  solarthermie: boolean;
  photovoltaik: boolean;
  denkmalschutz: boolean;
  ferienhaus: boolean;
  nichtDauerhaftBewohnt: boolean;
  schwimmbad: boolean;
  de1kzdachbelag: boolean;
  gebkategorie: string;
  gebklasse: string;
  feuerrohbau: boolean;
}

export const merkmaleReducer = function (
  sectionState: Merkmale = null,
  action: AnyAction,
  appState: IAppState,
): Merkmale {
  switch (action.type) {
    case GlobalActions.IMMOBILIE_UEBERNEHMEN: {
      if (action.payload.appId !== App.VWG || action.payload.angebotsId !== appState.angebotsId) {
        return sectionState;
      }

      const immobilie = action.payload.immobilie as Immobilie;
      const newState: Merkmale = cloneDeep(sectionState);
      newState.keller = valueOrDefault(immobilie.kellerVorhanden, false);
      newState.kellerMitWohnflaeche = immobilie.kellerVorhanden
        ? immobilie.kellerMitWohnflaeche
        : null;
      newState.ferienhaus = valueOrDefault(immobilie.ferienhaus, false);
      newState.flachdach = valueOrDefault(immobilie.flachdach, false);
      return newState;
    }
    case VwgActions.MERKMALE_CHANGED: {
      const newState: Merkmale = cloneDeep(sectionState);
      if (newState.kellerMitWohnflaeche && action.payload.keller === false) {
        newState.kellerMitWohnflaeche = false;
      }
      return { ...newState, ...action.payload };
    }
    case VwgActions.BAUJAHR_CHANGED: {
      const newState: Merkmale = cloneDeep(sectionState);
      if (!isDenkmalschutzSelectableByValue(action.payload.baujahr)) {
        newState.denkmalschutz = false;
      }
      return newState;
    }
    case VwgActions.WOHNFLAECHE_CHANGED: {
      const newState: Merkmale = cloneDeep(sectionState);
      if (!isSchwimmbadSelectableByValue(action.payload.wohnflaeche)) {
        newState.schwimmbad = false;
      }
      return newState;
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED: {
      const actionPayload: VwgVorbelegungdaten = action.payload;
      const merkmale: VwgVorvertragsdatenMerkmale = actionPayload.merkmale;
      const newState: Merkmale = cloneDeep(sectionState);
      if (actionPayload.forApp === App.VWG && actionPayload.angebotsId === appState.angebotsId) {
        newState.keller = merkmale.kellerVorhanden;
        newState.dachbelag = merkmale.dachbelagWeich;
        newState.solarthermie = merkmale.solarthermie;
        newState.photovoltaik = merkmale.photovoltaik;
        newState.nichtDauerhaftBewohnt = merkmale.leerstehend;
        newState.ferienhaus = merkmale.ferienhaus;
        newState.denkmalschutz = merkmale.denkmalschutz;
        newState.schwimmbad = merkmale.schwimmbad;
        newState.kellerMitWohnflaeche = merkmale.kellerMitWohnflaeche;
        newState.geothermie = merkmale.geothermie;
        newState.flachdach = merkmale.flachdach;
        newState.holzhaus = merkmale.holzhaus;
      }
      return newState;
    }
    default:
      return sectionState;
  }
};

function valueOrDefault(value: boolean | undefined, defaultValue: boolean): boolean {
  return value || defaultValue;
}
